<template>
    <ButtonDefault
      :color="color"
      :size="mini ? 'xs' : size"
      :type-style="mini ? 'outline' : styleButton"
      :disabled="isGeneratingPdf"
      :class-add="classAdd"
      @click="generatePdf"
    >
      <template v-if="isGeneratingPdf">
        <div class="animate-spin inline-block size-4 border-3 border-current border-t-transparent text-red-600 rounded-full dark:text-red-500" role="status" aria-label="loading">
          <span class="sr-only">Loading...</span>
        </div>

      </template>
      <template v-else>
        <MdiIcon
          v-if="mini && !isSimplePrint"
          icono="mdiPrinterEye"
        />
        <DocumentArrowDownIcon
          v-else
          class="shrink-0 size-4"
        />
      </template>
      <span v-if="showText && !mini">{{ buttonText }}</span>
    </ButtonDefault>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { DocumentArrowDownIcon } from '@heroicons/vue/24/outline'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import type { ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'
import type { IFormComponent, IFormStructure } from '@/modules/Configuracion/interfaces/IFormComponent'
import { tipoImpresionAsistencialDefault } from '@/modules/Core/usings/constantes'
import { generateDocMedico } from '@/modules/Core/usings/impresion'
import { admisionService } from '@/services'
import { usePdf } from '@/modules/Core/composables/usePdf'
import { useAdmisionTechComponents } from '../composables/useAdmisionTechComponents'

const {  generatePdf: generatePdfCore, generarDocumentoAtencion } = usePdf()
const { extractTechComponents } = useAdmisionTechComponents()

const isGeneratingPdf = ref(false)
const technologiaDB = ref<ICitaTechnologiasSalud>()
const admisionDB = ref<ICitaAdmision>()
const technologiaSeleccionada = ref<ICitaTechnologiasSalud | null>(null)
const calculatedComponents = ref<IFormComponent[]>([])

/**
 * Calcula los componentes automáticamente desde la admisión
 * cuando no se proporcionan components ni technologia explícitamente
 */
const calculateComponentsFromAdmision = async (admision: ICitaAdmision): Promise<IFormComponent[]> => {
  if (!admision) return []

  try {
    // Usar el composable para extraer componentes de tecnologías tramitadas
    const components = await extractTechComponents(admision, ['TRAMITADO'])
    calculatedComponents.value = components

    return components
  } catch (error) {
    console.error('Error calculando componentes desde admisión:', error)
    return []
  }
}
const props = defineProps({
  // Datos principales
  id: {
    type: Number,
    default: null
  },
  admision: {
    type: Object as () => ICitaAdmision,
    default: null
  },
  technologia: {
    type: Object as () => ICitaTechnologiasSalud,
    default: null
  },
  /**
   * Componentes de formulario para el PDF.
   * Si no se proporcionan pero se envía `admision`,
   * se calcularán automáticamente usando useAdmisionTechComponents
   */
  components: {
    type: Array as () => IFormComponent[],
    default: null
  },
  graficas: {
    type: Array,
    default: null
  },
  odontogramas: {
    type: Array as () => { titulo: string, svg: string }[],
    default: null
  },
  // Opciones de generación
  tipoImpresion: {
    type: String as () => tipoImpresionAsistencialDefault,
    default: tipoImpresionAsistencialDefault.HISTORIA
  },
  isPreview: {
    type: Boolean,
    default: true
  },
  includeGraficas: {
    type: Boolean,
    default: false
  },
  includeOdontogramas: {
    type: Boolean,
    default: false
  },
  returnAsBlob: {
    type: Boolean,
    default: false
  },
  // Opciones de UI
  color: {
    type: String,
    default: 'red'
  },
  size: {
    type: String,
    default: 'sm'
  },
  showText: {
    type: Boolean,
    default: true
  },
  buttonText: {
    type: String,
    default: 'PDF'
  },
  isSimplePrint: {
    type: Boolean,
    default: false
  },
  mini: {
    type: Boolean,
    default: false
  },
  styleButton: {
    type: String,
    default: 'default'
  },
  classAdd: {
    type: String,
    default: ''
  }
})

onMounted(async () => {
  if (props.isSimplePrint) {
      await cargarAdmision()
  }
})

const generatePdf = async () => {
  isGeneratingPdf.value = true

  try {
    if(props.technologia) {
      const generadoDocumento = await generarDocumentoAtencion(props.technologia, props.tipoImpresion)
      if(generadoDocumento) return
    }

    if (props.isSimplePrint) {
      await cargarAdmision()

      // Determinar qué componentes usar
      let componentsToUse = props.components

      // Si no hay components pero hay admisión, calcularlos automáticamente
      if (!componentsToUse && (admisionDB.value || props.admision)) {
        const admisionData = admisionDB.value || props.admision
        if (admisionData) {
          componentsToUse = await calculateComponentsFromAdmision(admisionData)
        }
      }

      const formData = {
        technologia: technologiaDB.value || props.technologia || technologiaSeleccionada.value,
        admision: admisionDB.value || props.admision,
        components: componentsToUse
      } as IFormStructure

      await generateDocMedico({...formData}, props.tipoImpresion)
      return
    }

    // Determinar componentes para el caso no-simple
    let componentsForPdf = props.components

    // Si no hay components ni technologia pero hay admision, calcular componentes
    if (!componentsForPdf && !props.technologia && props.admision) {
      componentsForPdf = await calculateComponentsFromAdmision(props.admision)
    }

    if (componentsForPdf && props.technologia && props.admision) {
      await generatePdfCore(
        {
          components: componentsForPdf,
          technologia: props.technologia,
          admision: props.admision,
          graficas: props.graficas || undefined,
          odontogramas: props.odontogramas || undefined
        },
        {
          tipoImpresion: props.tipoImpresion,
          isPreview: props.isPreview,
          includeGraficas: props.includeGraficas,
          includeOdontogramas: props.includeOdontogramas,
          returnAsBlob: props.returnAsBlob
        }
      )
      return
    }

    // Si tenemos un ID específico, lo usamos
    if (props.id) {
      await generatePdfCore(props.id, {
        tipoImpresion: props.tipoImpresion,
        isPreview: props.isPreview,
        includeGraficas: props.includeGraficas,
        includeOdontogramas: props.includeOdontogramas,
        returnAsBlob: props.returnAsBlob
      })
      return
    }

    // Si no tenemos ID pero tenemos admisión, procesamos las tecnologías
    if (props.admision?.technologias) {
      const technologias = props.admision.technologias.filter(e => e.cittEstado === 'TRAMITADO')

      if (technologias.length === 0) {
        console.warn('No hay tecnologías tramitadas para generar PDF')
        return
      }

      // Si hay múltiples tecnologías, generamos PDF para cada una
      for (const technologia of technologias) {
        if (technologia.cittId) {
          await generatePdfCore(technologia.cittId, {
            tipoImpresion: props.tipoImpresion,
            isPreview: props.isPreview,
            includeGraficas: props.includeGraficas,
            includeOdontogramas: props.includeOdontogramas,
            returnAsBlob: props.returnAsBlob
          })
        }
      }
    } else {
      console.warn('No se proporcionó ID ni datos válidos para generar PDF')
    }
  } catch (error) {
    console.error('Error al generar PDF:', error)
  } finally {
    isGeneratingPdf.value = false
  }
}
const cargarTechnologia = async (item) => {
  if (!item) return []
  return item.filter(e => e.cittEstado === 'TRAMITADO')
}

const cargarAdmision = async () => {
  if (props.admision) {
    if (!props.technologia) {
      technologiaSeleccionada.value = await cargarTechnologia(props.admision.technologias || [])
    }
    return props.admision
  }

  if (props.id) {
    const { data } = await admisionService.getAdmisionByTechId(props.id, { withAssociationOrders: true, withPacienteSnapshot: true })
    const dataAdmision = data.body
    admisionDB.value = dataAdmision
    technologiaDB.value = dataAdmision.technologias?.find((item: ICitaTechnologiasSalud) => item.cittId === props.id)
    return data.body
  }

  return null
}

</script>
